import {React, useState} from 'react';
import { Card, CardContent, Typography, Button, Modal, ModalDialog, ModalClose, Input, Alert, IconButton, Stack, ListItem, Accordion,  ListItemContent, AccordionSummary, AccordionDetails, Tooltip , DialogTitle, Switch} from '@mui/joy';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Lock, LockOpen, Delete, Close, Add, FilterAlt, Edit } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import SpinSelector from './SpinSelector';
import ParitySelector from './ParitySelector';
import LineshapeSelector from './LineshapeSelector';
import LineshapeParameterModal from './LineshapeParameterModal';
import PDGSearchbar from './PDGSearchbar';
import Toast from './Toast';
import { identicalNodes } from '../Internal/helpers';

export default function IsobarCard(props) {
    const { isobar, variant, color } = props;   
    const [isobarModal, setIsobarModal] = useState(false);
    const [newSpin, setNewSpin] = useState(null);
    const [newParity, setNewParity] = useState(null);
    const [newLineshapeParams, setNewLineshapeParams] = useState(null);

    const [newLabel, setNewLabel] = useState("New Resonance");
    const [newPartityConserved, setNewPartityConserved] = useState(true);

    const [updatedSpin, setUpdatedSpin] = useState(null);
    const [updatedParity, setUpdatedParity] = useState(null);
    const [setUpdate, setSetUpdate] = useState(null);
    const [tryDelete, setTryDelete] = useState(false);

    // Lineshape editing state
    const [lineshapeModal, setLineshapeModal] = useState(false);
    const [editingLineshapeResonance, setEditingLineshapeResonance] = useState(null);

    const [messageOpen, setMessageOpen] = useState(false);

    const dispatch = useDispatch();
    const globalIsobars = useSelector((state) => state.intermediateState.isobars);
    const currentIsobar = useSelector((state) => state.intermediateState.isobars[isobar.label]);
    const finalStateData = useSelector((state) => state.finalState.finalStateData);
    const identicalParticles = useSelector((state) => state.identicalParticles);
    const fillIdentical = useSelector((state) => state.identicalParticles.symmetrizationEnabled);
    const topologyFilter = useSelector((state) => state.topologyFilter);
    const lineshapeSchemas = useSelector(state => state.lineshapeSchemas.schemas);


    if (!currentIsobar) {
        return null;
    }
    const identicalVariants = identicalNodes(currentIsobar.tuple, Object.values(identicalParticles.groups));
    const internalNodes = currentIsobar.tuple.flat(Infinity);
    const particleNamesLabel = internalNodes.sort().map(n => finalStateData[n] ? finalStateData[n].name : "").join(" ");
    const tupleNodes = currentIsobar.tuple.flat(Infinity).sort();
    const filterKey = "[" + tupleNodes.join(",") + "]";
    const isFiltered = topologyFilter.hasOwnProperty(filterKey);
    const handleFilterToggle = (e) => {
        e.stopPropagation();
        if (isFiltered) {
            dispatch({ type: 'REMOVE_FILTER', payload: filterKey });
        } else {
            const filterValue = Object.fromEntries(tupleNodes.map(n => [n, true]));
            dispatch({ type: 'ADD_FILTER', payload: { key: filterKey, value: filterValue } });
        }
    };
    if (!currentIsobar) {
        return null;
    }
    const handleModal = () => {
        setIsobarModal(!isobarModal);
    }

    const addResonance = (addIdentical=true) => {
        const updatedIsobars = {
            ...globalIsobars,
                [isobar.label]: {
                    ...currentIsobar,
                    resonances : {
                        ...currentIsobar.resonances,
                        [newLabel]: {
                            name: newLabel,
                            spin: newSpin,
                            parity: newParity,
                            tuple: currentIsobar.tuple,
                            parityConserved: newPartityConserved,
                            lineshape: newLineshapeParams
                        }
                    }
            },
        };

        if (addIdentical) {
            
            identicalVariants.forEach(variant => {
                updatedIsobars[variant] = {
                    ...updatedIsobars[variant],
                    resonances : {
                        ...updatedIsobars[variant].resonances,
                        [newLabel]: {
                            name: newLabel,
                            spin: newSpin,
                            parity: newParity,
                            tuple: updatedIsobars[variant].tuple,
                            parityConserved: newPartityConserved,
                            lineshape: newLineshapeParams
                        }
                    }
                }
            });
        }

        dispatch({ type: 'SET_ISOBAR', payload: updatedIsobars });
        setIsobarModal(false);
        setNewSpin(null);
        setNewParity(null);
        setNewLineshapeParams(null);
        setNewLabel("New Resonance");
        setNewPartityConserved(true);
    }

    const changeResonance = (addIdentical=true) => {
        const updatedIsobars = {
            ...globalIsobars,
                [isobar.label]: {
                    ...currentIsobar,
                    resonances : {
                        ...currentIsobar.resonances,
                        [setUpdate]: {
                            ...currentIsobar.resonances[setUpdate],
                            spin: updatedSpin,
                            parity: updatedParity,
                            // parityConserved: newPartityConserved
                        }
                    }
            },
        };

        if (addIdentical) {
            identicalVariants.forEach(variant => {
                updatedIsobars[variant] = {
                    ...updatedIsobars[variant],
                    resonances : {
                        ...updatedIsobars[variant].resonances,
                        [setUpdate]: {
                            ...updatedIsobars[variant].resonances[setUpdate],
                            spin: updatedSpin,
                            parity: updatedParity,
                            // tuple: updatedIsobars[variant].tuple,
                            // parityConserved: newPartityConserved
                        }
                    }
                }
            });
        }

        dispatch({ type: 'SET_ISOBAR', payload: updatedIsobars });
        setSetUpdate(null);
        setIsobarModal(false);
        setUpdatedSpin(null);
        setUpdatedParity(null);
        setNewPartityConserved(true);
    }

    const deleteResonance = (deleteIdentical=false) => {
        const updatedIsobars = {
            ...globalIsobars,
                [isobar.label]: {
                    ...currentIsobar,
                    resonances : {
                        ...currentIsobar.resonances,
                        [setUpdate]: null
                    }
            },
        };
        delete updatedIsobars[isobar.label].resonances[setUpdate];
        if (deleteIdentical) {
            identicalVariants.forEach(variant => {
                delete updatedIsobars[variant].resonances[setUpdate];
            });
        }
        dispatch({ type: 'SET_ISOBAR', payload: updatedIsobars });
        setIsobarModal(false);
        setUpdatedSpin(null);
        setUpdatedParity(null);
        setSetUpdate(null);
        setNewPartityConserved(true);
    }

    const handleUpdateToggle = (resonanceName) => {
        if (resonanceName === setUpdate) {
            setUpdatedSpin(null);
            setUpdatedParity(null);
            setTryDelete(false);
            setSetUpdate(null);
        }
        else {
            setUpdatedSpin(currentIsobar.resonances[resonanceName].spin);
            setUpdatedParity(currentIsobar.resonances[resonanceName].parity);
            setSetUpdate(resonanceName);
        }
    }

    const handleLineshapeEdit = (resonanceName) => {
        setEditingLineshapeResonance(resonanceName);
        setLineshapeModal(true);
    }

    const handleLineshapeSave = (lineshapeParams) => {
        const updatedIsobars = {
            ...globalIsobars,
            [isobar.label]: {
                ...currentIsobar,
                resonances: {
                    ...currentIsobar.resonances,
                    [editingLineshapeResonance]: {
                        ...currentIsobar.resonances[editingLineshapeResonance],
                        lineshape: lineshapeParams
                    }
                }
            }
        };

        // Update identical variants if enabled
        if (fillIdentical) {
            identicalVariants.forEach(variant => {
                updatedIsobars[variant] = {
                    ...updatedIsobars[variant],
                    resonances: {
                        ...updatedIsobars[variant].resonances,
                        [editingLineshapeResonance]: {
                            ...updatedIsobars[variant].resonances[editingLineshapeResonance],
                            lineshape: lineshapeParams
                        }
                    }
                }
            });
        }

        dispatch({ type: 'SET_ISOBAR', payload: updatedIsobars });
        setLineshapeModal(false);
        setEditingLineshapeResonance(null);
    }

    const handleLineshapeModalClose = () => {
        setLineshapeModal(false);
        setEditingLineshapeResonance(null);
    }


    const buttonState = currentIsobar.resonances.hasOwnProperty(newLabel) ? false : true;
    const spinPartiySet = (newSpin === null || newParity === null) ? false : true;

    return (
        <>
            <Card variant={variant} color={color} sx={{ width: '100%', marginBottom: '1rem', overflow: 'auto' }}>
                <Tooltip title={particleNamesLabel}>
                <CardContent >
                    <Accordion >
                        <AccordionSummary id={"Isobar" + isobar.label}>
                        <ListItemContent>
                            <Typography level="title-md">{particleNamesLabel}</Typography>
                            <Typography level="body-sm">
                                {Object.getOwnPropertyNames(currentIsobar.resonances).length} resonances
                            </Typography>
                        </ListItemContent>
                        <Tooltip title={isFiltered ? "Remove filter" : "Filter by this isobar"} placement="left">
                            <IconButton
                                size="sm"
                                variant={isFiltered ? 'solid' : 'soft'}
                                color={isFiltered ? 'primary' : 'neutral'}
                                onClick={handleFilterToggle}
                            >
                                <FilterAlt fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        </AccordionSummary>
                            <AccordionDetails>
                            <Stack direction="column" spacing={1} sx={{overflowY: 'auto', maxHeight: '20rem'}}>
                            <Button onClick={handleModal} startIcon={<Add />}>
                                    Add Resonance
                                </Button>
                            

                            {
                                currentIsobar.resonances ? Object.getOwnPropertyNames(currentIsobar.resonances).map((resonanceName) => {
                                    return (
                                    // <MenuItem>
                                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                                        <ListItem>
                                                <Tooltip title={resonanceName + (
                                                    (currentIsobar.resonances[resonanceName].parityConserved === true || currentIsobar.resonances[resonanceName].parityConserved === undefined) ? ": conserves parity" : ": does not conserve parity"
                                                )}>
                                                <Typography level="body-md" sx={{flexGrow: 1}} maxWidth={(resonanceName === setUpdate) ? 110: null} noWrap={true}>
                                                    {resonanceName}
                                                </Typography>
                                                </Tooltip> 
                                                <SpinSelector value={(resonanceName === setUpdate) ? updatedSpin : currentIsobar.resonances[resonanceName].spin} locked={!(setUpdate === resonanceName)} onChange={(e, newValue) => {setUpdatedSpin(newValue)}} tooltip = ""/>
                                                <ParitySelector value={(resonanceName === setUpdate) ? updatedParity :currentIsobar.resonances[resonanceName].parity} locked={!(setUpdate === resonanceName)} onChange={(e, newValue) => {setUpdatedParity(newValue)}} tooltip = ""/>
                                        </ListItem>
                                        <IconButton onClick={() => {handleUpdateToggle(resonanceName)}} color="primary" variant="solid" disabled={false}>
                                        {(setUpdate === resonanceName) ? <LockOpen></LockOpen> : <Lock></Lock>}
                                        </IconButton>
                                        {currentIsobar.resonances[resonanceName].lineshape && (
                                            <Tooltip title="Edit Lineshape">
                                                <IconButton 
                                                    onClick={() => handleLineshapeEdit(resonanceName)} 
                                                    color="neutral" 
                                                    variant="soft" 
                                                    disabled={false}
                                                >
                                                    <Edit />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {
                                            (setUpdate === resonanceName) ? <Button onClick={() => changeResonance(fillIdentical)} color="success" variant="solid" disabled={updatedSpin === null || updatedParity === null}>
                                                Update
                                            </Button> : null
                                        }
                                        {
                                            (setUpdate === resonanceName) ? <IconButton onClick={() => setTryDelete(!tryDelete)} color={(tryDelete) ?"neutral":"danger"} variant="solid" disabled={false}>
                                                {(!tryDelete) ? <Delete></Delete> : <Close></Close>}
                                            </IconButton> : null
                                        }
                                        {
                                            (setUpdate === resonanceName && tryDelete) ? <Button onClick={() => {deleteResonance(fillIdentical); setTryDelete(!tryDelete)}} color="danger" variant="solid" disabled={false}>
                                                Confirm
                                            </Button> : null
                                        }
                                        </Stack>
                                    )
                                }) : null
                            }
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                </CardContent>
                </Tooltip>
            </Card>

            <Modal open={isobarModal} onClose={handleModal} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <ModalDialog>
                {
                messageOpen ? <Toast message="Parity not defined! Select parity by hand. If decay is weak, set conserve parity to false!" open={messageOpen} setOpen={setMessageOpen}/>
                : null
                }
                    <ModalClose onClick={handleModal} />
                    <DialogTitle>{particleNamesLabel}</DialogTitle>
                    <Input value={newLabel} disabled={false} onChange={(e) => {setNewLabel(e.target.value)}} />
                    <SpinSelector value={newSpin} onChange={(e, newValue) => {setNewSpin(newValue)}} locked={false} toolTip="Select the spin of the isobar" />
                    <ParitySelector value={newParity} onChange={(e, newValue) => {setNewParity(newValue)}} locked={false} toolTip="Select the parity of the isobar" />
                    <LineshapeSelector value={newLineshapeParams} onChange={(e, newValue) => {setNewLineshapeParams(newValue)}} locked={false} toolTip="Select optional lineshape for the resonance" />
                    <Button onClick={() => addResonance(fillIdentical)} disabled={!buttonState || !spinPartiySet}>
                        Add Resonance
                    </Button>
                    <PDGSearchbar onParticleSelect={
                        (newValue) => {
                            if (newValue) {
                                setNewLabel(newValue.label);
                                setNewSpin(newValue.spin);
                                if (newValue.parity != 0) {
                                    setNewParity(newValue.parity);
                                }
                                else {
                                    setNewParity(null);
                                    setMessageOpen(true);
                                }
                                
                            }
                        }
                    }/>
                    <FormControlLabel label={"Parity Conservation"} control={
                        <Switch checked={newPartityConserved} onChange={(e) => {setNewPartityConserved(e.target.checked)}}
                        />}
                    sx={ {Items: 'center', justifyContent: 'left', marginTop: '1rem', marginBottom: '1rem'} }/>
                    {
                        !buttonState ? <Alert variant={variant} color="warning">
                            Isobar with this name already exists
                      </Alert> : null
                    } {
                        !spinPartiySet ? <Alert variant={variant} color="warning">
                            Please select a spin and parity
                      </Alert> : null
                    }
                    {
                        ((identicalVariants.length > 1) && (fillIdentical)) ? <Alert variant={variant} color="info">
                            Resonance will be added to {identicalVariants.length - 1} other identical node{identicalVariants.length - 1 === 1 ? '' : 's'}.
                            <br />
                            Please ensure that the symmetrization settings are correct.
                        </Alert> : null
                    }

                </ModalDialog>
            </Modal>

            {/* Lineshape Parameter Modal */}
            <LineshapeParameterModal
                open={lineshapeModal}
                onClose={handleLineshapeModalClose}
                onSave={handleLineshapeSave}
                lineshapeSchema={editingLineshapeResonance && lineshapeSchemas?.lineshapes ? 
                    Object.values(lineshapeSchemas.lineshapes).find(schema => 
                        schema.lineshape_type === currentIsobar.resonances[editingLineshapeResonance]?.lineshape?.lineshape_type
                    ) : null}
                initialValues={editingLineshapeResonance ? currentIsobar.resonances[editingLineshapeResonance]?.lineshape : null}
            />
        </>
    )
}